import { useState, useEffect, useSyncExternalStore } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import WebsiteLogo from "@/components/common/WebsiteLogo";
import { useAuth } from "@/context/AuthContext";
import { useEmailVerifiedToast } from "@/hooks/useEmailVerifiedToast";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, type UserCredential } from "firebase/auth";
import { handleFirebaseError } from "@/utils/errorUtils";
import { toInternalPath } from "@/utils/toInternalPath";
import { enforceProAccessGate } from "@/lib/proAccessGate";
import { mintApexSession } from "@/lib/apexSessionMint";
import { emitAnalyticsEvent } from "@/lib/analytics/emitEvent";
import { ANALYTICS_EVENTS } from "@/lib/analytics/eventNames";
import {
  boundAuthCall,
  isAuthTransportTimeout,
  authFailureAttestationMessage,
  AUTH_ATTESTATION_REJECTED_MESSAGE,
  AUTH_INTERACTIVE_BUDGET_MS,
} from "@/lib/auth-transport";
import {
  getAppCheckSnapshot,
  isInsideRefusalThrottle,
  readAppCheckEvidence,
  reloadForFreshAttestation,
  subscribeAppCheckOutcome,
} from "@/lib/appCheckOutcome";
import { persistLoginUserRecord } from "@/lib/loginUserRecord";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, currentUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // KB-3352 AC-4: inside App Check's refusal throttle no sign-in can succeed
  // from this tab, so the buttons that would invite one are replaced by a
  // reload, the one reset a user has for the SDK's in-memory throttle.
  const appCheck = useSyncExternalStore(subscribeAppCheckOutcome, getAppCheckSnapshot);
  const attestationRefused = isInsideRefusalThrottle(appCheck, Date.now());
  
  // Handle email verification success toast
  useEmailVerifiedToast();
  
  // Extract the returnUrl from query parameters. Attacker-controlled, so it is
  // normalised to a same-origin path before it ever reaches navigate().
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = toInternalPath(searchParams.get("returnUrl"));

  // Redirect anonymous users to create account page
  useEffect(() => {
    if (currentUser?.isAnonymous) {
      navigate('/create-account');
    }
  }, [currentUser, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUBMIT, { method: "email" });

    console.log("Login attempt with email:", email);

    try {
      const user = await login(email, password);
      console.log("Credential accepted for user:", user.email);

      // ENFORCE ALLOWLIST (KB-3133). A correct password proves who you are, not
      // that anyone approved you for pro. Until this line existed, this door ran
      // straight from `login()` to `navigate()` while the Google button one
      // function below did a profile read, a role check and a sign-out — so
      // KB-3064 closed the class for a click and left it open for a password.
      //
      // Same helper, same read, same two sentences as every other door: see
      // `proAccessGate.ts`. Nothing is decided here, so the doors cannot drift.
      const verdict = await enforceProAccessGate(user.uid, "email");
      if (verdict.outcome !== "allowed") {
        // The gate has already signed them out, rendered the right sentence and
        // recorded the refusal. Returning — rather than throwing — keeps this
        // out of the catch below, whose toast would replace that sentence with
        // "Login failed: …" and whose analytics would record `error.code`
        // instead of the refusal. (Same reasoning as the Google path's
        // round-2 finding 5.)
        setIsLoading(false);
        return;
      }

      // Only now is this a successful login. Both of these used to run before
      // any check: the funnel counted a rejected stranger as `auth_login_success`,
      // and `toast.success("Login successful!")` — fired from inside
      // `AuthContext.login` — told them so, moments before the refusal.
      emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, { method: "email" });
      toast.success("Login successful!");

      // Store basic user info in localStorage for app state. Shared with
      // AuthContext.login's late-settle reconciler so the two cannot drift
      // (KB-3033 round-1 #1).
      persistLoginUserRecord(user);

      // KB-3235: mint the apex `__session` cookie so this director is recognised
      // on kidsbooked.com too. AFTER the gate allowed them; fire-and-forget so it
      // cannot fail or delay this sign-in. See `apexSessionMint.ts`.
      void mintApexSession(user);

      // Navigate to the returnUrl after successful login
      navigate(returnUrl);
    } catch (error: any) {
      // Error handling is done in the login function
      console.error("Login process failed:", error);
      emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
        method: "email",
        reason: error?.code || "unknown",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUBMIT, { method: "google" });
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    console.log("Starting Google login process");

    // Declared outside the try so the timeout handler can still reach it, and
    // assigned inside so a synchronous throw cannot escape the catch.
    let pendingPopup: Promise<UserCredential> | undefined;
    try {
      // KB-3033 round-1 #3: bounded for the same reason the email path is.
      // `signInWithPopup` is App Check-gated on both sides of the human —
      // `_validateOrigin`→`_getProjectConfig` before the popup opens and
      // `signInWithIdp` after it closes — so a wedged reCAPTCHA hangs it
      // forever. That matters here beyond the Google button itself: `isLoading`
      // is ONE state var gating both buttons, so an unbounded hang leaves the
      // email form stuck on "Logging in…" — the exact symptom AC-3 exists to
      // eliminate, one button away. The budget is the interactive one, because
      // this promise legitimately spans a person reading an account chooser.
      pendingPopup = signInWithPopup(auth, provider);
      const result = await boundAuthCall(
        pendingPopup,
        'signInWithPopup',
        AUTH_INTERACTIVE_BUDGET_MS,
      );
      const user = result.user;
      console.log("Google login successful, user:", user.email);

      // ENFORCE ALLOWLIST. KB-3133 moved the body of this gate into
      // `proAccessGate.ts` WITHOUT changing what it decides or what it renders:
      // the read, the two sentences, the sign-out on both arms and the two
      // analytics reasons are the same, and the KB-3064 suite still pins them
      // through this call site. It moved because the email door had to enforce
      // the identical gate, and a hand-copy is how the two doors drift apart
      // again — which is the defect KB-3133 exists to fix, not to repeat.
      //
      // The reasoning that shaped it is preserved with the code: why `role` and
      // not doc existence, why an owner read by uid, why a failed read must not
      // render as "not authorized", and why the deleted approved-request arm
      // stays deleted. See that module and `proAccessRole.ts`.
      const verdict = await enforceProAccessGate(user.uid, "google");
      if (verdict.outcome !== "allowed") {
        setIsLoading(false);
        return;
      }
      const userProfile = verdict.profile;

      localStorage.setItem("user", JSON.stringify({
        id: user.uid,
        email: user.email,
        role: userProfile.role || "camp_user",
        organizationRole: userProfile.organizationRole || "Organization owner",
        firstName: userProfile.firstName || (user.displayName ? user.displayName.split(' ')[0] : "User"),
        lastName: userProfile.lastName || (user.displayName ? user.displayName.split(' ')[1] : "")
      }));

      toast.success("Login successful!");
      emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS, { method: "google" });

      // KB-3235: mint the apex `__session` cookie (see the email path above).
      void mintApexSession(user);

      // Navigate to the returnUrl after successful login
      navigate(returnUrl);
    } catch (error: any) {
      console.error("Google login error:", error);
      if (isAuthTransportTimeout(error)) {
        // ROUND-2 B1 — the important one. `boundAuthCall` does not cancel
        // `signInWithPopup`, so a popup the user completes at 130 s still
        // resolves, and the SDK makes that account the live session. Everything
        // below the bound — `getOwnUserProfile` and the `signOut` +
        // "not authorized" toast — is skipped, and AuthContext's
        // onAuthStateChanged does no allowlist work and never signs anyone out.
        //
        // So an unapproved Google account would keep a live session that never
        // passed the gate. The bound created that hole; this closes it.
        //
        // Signing out is right here even though the email path deliberately
        // does NOT (dispatcher ruling): there, the credential had already
        // passed every check the app makes, so keeping it was safe. Here the
        // check itself is what got skipped, so the session is unverified by
        // construction. No uid guard, for the round-2 B2 reason — the SDK has
        // already switched `currentUser` to this credential before we see it,
        // so this IS the live session.
        void pendingPopup?.then(
          async () => {
            console.warn(
              'Google popup settled AFTER its bound expired; signing out — the account never ' +
                'passed the allowlist check.',
            );
            try {
              await signOut(auth);
            } catch (signOutError) {
              console.error('Failed to sign out a late Google popup session:', signOutError);
            }
          },
          () => {},
        );
      }
      // A transport timeout carries no `auth/` code, so handleFirebaseError
      // would flatten it to "Authentication error: signInWithPopup did not
      // settle within …" — the raw-jargon failure AC-1/AC-4 exist to kill.
      if (isAuthTransportTimeout(error)) {
        // KB-3352: one read of the App Check outcome drives the copy, the tag
        // and the breadcrumb. Captured because this door otherwise reported
        // nothing at all, and a recurrence must be attributable from Sentry.
        // Awaited: it asks the SDK whether it still holds a valid token (round-2 B3).
        const attestation = await readAppCheckEvidence();
        Sentry.addBreadcrumb({
          category: "auth-bootstrap",
          level: "error",
          message: `google login: ${error.awaitName} exceeded ${error.boundMs}ms`,
        });
        Sentry.addBreadcrumb({
          category: "auth-bootstrap",
          level: "error",
          message: attestation.message,
          data: attestation.data,
        });
        Sentry.captureException(error, {
          tags: {
            source: "LoginPage.handleGoogleLogin",
            errorType: "auth-transport-timeout",
            appcheck_outcome: attestation.outcome,
          },
        });
        toast.error(authFailureAttestationMessage(attestation.outcome, "google"));
      } else {
        toast.error("Google login failed: " + handleFirebaseError(error));
      }
      emitAnalyticsEvent(ANALYTICS_EVENTS.AUTH_LOGIN_FAILURE, {
        method: "google",
        reason: error?.code || "unknown",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return <div className="min-h-screen flex items-center justify-center bg-kidsbooked-lightGray">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <WebsiteLogo width={180} className="mb-4" />
          <CardTitle className="text-2xl font-bold">Welcome to KidsBooked Pro</CardTitle>
          <CardDescription>Where camps manage their KidsBooked data. Please send feedback or issues to support@kidsbooked.com.</CardDescription>
        </CardHeader>
        <CardContent>
          {attestationRefused ? (
            <div role="alert" className="space-y-4">
              <p className="text-sm text-red-700">{AUTH_ATTESTATION_REJECTED_MESSAGE}</p>
              <Button type="button" className="w-full btn-primary" onClick={() => reloadForFreshAttestation()}>
                Reload page
              </Button>
            </div>
          ) : (
          <>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input id="email" placeholder="name@example.com" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="form-input" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <label htmlFor="password" className="text-sm font-medium"> {/* SECURITY_AUDIT_PASS */}
                  Password
                </label>
                <Link to="/forgot-password" className="text-sm text-kidsbooked-blue hover:underline"> {/* SECURITY_AUDIT_PASS */}
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="form-input" /> {/* SECURITY_AUDIT_PASS */}
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={isLoading}>
              {isLoading ? "Logging in..." : "Login with Email"}
            </Button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button variant="outline" type="button" className="w-full" onClick={handleGoogleLogin} disabled={isLoading}>
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" className="mr-2">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Google
          </Button>
          </>
          )}
          
          {/* DEBUG: Test toast buttons - only show on localhost */}
          {(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && (
            <div className="mt-4 p-2 border border-red-300 bg-red-50 rounded">
              <p className="text-xs text-red-600 mb-2">DEBUG: Toast Test (localhost only)</p>
              <div className="flex gap-2">
                <Button 
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    console.log('🧪 [DEBUG] Manual toast test triggered');
                    toast.success("Test Toast", {
                      description: "This is a test toast to verify the toast system works.",
                      duration: 5000,
                      dismissible: true
                    });
                  }}
                >
                  Test Toast
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    console.log('🧪 [DEBUG] Clearing email verification session storage');
                    sessionStorage.removeItem('kb_email_verified_toast_shown');
                    toast.info("Session cleared", {
                      description: "Email verification toast session has been reset.",
                      duration: 3000
                    });
                  }}
                >
                  Clear Session
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter>
          <div className="text-center w-full">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/request-account" className="text-kidsbooked-blue hover:underline">
                Create account
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </div>;
};

export default LoginPage;
